import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import QuratorLayout from '../components/qurator/QuratorLayout';
import API from '../config/api';

const DraftRead = () => {
  const [searchParams] = useSearchParams();
  const protocolId = searchParams.get('viewid');
  
  const [loading, setLoading] = useState(true);
  const [protocol, setProtocol] = useState(null);
  const [steps, setSteps] = useState([]);
  const [reagents, setReagents] = useState([]);
  const [educationalContent, setEducationalContent] = useState({});

  const fetchProtocol = useCallback(async () => {
    try {
      setLoading(true);
      const response = await API.get(`/qurator/protocols/${protocolId}`);
      
      if (response.data.success) {
        const data = response.data.protocol;
        setProtocol(data);
        
        // Use ordered steps from backend (ordered by step_number) or fallback to parsing string
        const stepArray = data.orderedSteps && data.orderedSteps.length > 0
          ? data.orderedSteps.filter(s => s && s.trim())
          : (data.step_of_protocol ? data.step_of_protocol.split('`').filter(s => s.trim()) : []);
        setSteps(stepArray);
        
        // Load reagents
        if (response.data.reagents) {
          setReagents(response.data.reagents);
        }
        
        // Load educational content
        const eduResponse = await API.get(`/qurator/protocols/${protocolId}/educational-content`);
        if (eduResponse.data.success) {
          setEducationalContent(eduResponse.data.content || {});
        }
      }
    } catch (error) {
      console.error('Error fetching protocol:', error);
    } finally {
      setLoading(false);
    }
  }, [protocolId]);

  useEffect(() => {
    if (protocolId) {
      fetchProtocol();
    }
  }, [protocolId, fetchProtocol]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <QuratorLayout>
        <div className="draft-read-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </QuratorLayout>
    );
  }

  if (!protocol) {
    return (
      <QuratorLayout>
        <div className="alert alert-danger">Protocol not found.</div>
      </QuratorLayout>
    );
  }

  return (
    <QuratorLayout>
      <div className="draft-read-container">
        {/* Header with title */}
        <div className="page-header">
          <div className="protocol-title">
            {protocol.draft_title}
            <span className="type-badge">
              <i className={`bi ${protocol.mr === 1 || protocol.mr === '1' ? 'bi-phone' : 'bi-display'}`}></i>
              {protocol.mr === 1 || protocol.mr === '1' ? 'Mixed Reality' : 'Virtual Reality'}
            </span>
          </div>
          <div className="meta-info">
            <div>
              <i className="bi bi-calendar3"></i>
              {formatDate(protocol.date)}
            </div>
            {(protocol.is_live === 1 || protocol.is_live === '1') && (
              <div>
                <i className="bi bi-check-circle-fill"></i>
                Published
              </div>
            )}
          </div>
        </div>

        {/* Description section */}
        {protocol.prt_description && (
          <div className="description-section">
            <div className="description-title">
              <i className="bi bi-info-circle"></i>
              Description
            </div>
            <div className="description-content">
              {protocol.prt_description}
            </div>
          </div>
        )}

        <div className="content-grid">
          {/* Steps Section */}
          <div className="content-card">
            <div className="card-header">
              <h5 className="section-title">
                <i className="bi bi-list-ol"></i>
                Experiment Steps
              </h5>
            </div>
            <ul className="step-list">
              {steps.length > 0 ? (
                steps.map((step, index) => (
                  <li key={index} className="step-item">
                    <div className="step-number">{index + 1}</div>
                    <div className="step-content">{step}</div>
                    {educationalContent[index] && educationalContent[index].questions && educationalContent[index].questions.length > 0 && (
                      <div className="step-educational-content">
                        <div className="educational-header">
                          <i className="bi bi-lightbulb"></i>
                          Educational Content
                        </div>
                        {educationalContent[index].questions.map((question, qIdx) => (
                          <div key={qIdx} className="educational-item">
                            <div className="question-category">
                              {question.question_type ? 
                                question.question_type.replace('_', ' ').split(' ').map(w => 
                                  w.charAt(0).toUpperCase() + w.slice(1)
                                ).join(' ') : 
                                'Question'}
                            </div>
                            <div className="question-text">{question.question_text}</div>
                            {question.flashcards && question.flashcards.length > 0 && (
                              <div className="flashcards-list">
                                {question.flashcards.map((flashcard, fcIdx) => (
                                  <div key={fcIdx} className="flashcard-item">
                                    <i className="bi bi-star-fill"></i>
                                    {flashcard}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </li>
                ))
              ) : (
                <li className="step-item">
                  <div className="text-muted text-center py-4">No steps available</div>
                </li>
              )}
            </ul>
          </div>

          {/* Reagents Section */}
          <div className="content-card">
            <div className="card-header">
              <h5 className="section-title">
                <i className="bi bi-flask"></i>
                Reagents
              </h5>
            </div>
            <div className="reagent-list">
              {reagents.length > 0 ? (
                reagents.map((reagent, index) => (
                  <div key={index} className="reagent-item">
                    <i className="bi bi-flask reagent-icon"></i>
                    <div className="reagent-details">
                      <div className="reagent-name">{reagent.reagent_name || 'Unnamed Reagent'}</div>
                      <div className="reagent-meta">
                        {reagent.reagent_vendor && <div>Vendor: {reagent.reagent_vendor}</div>}
                        {reagent.reagent_lot && <div>Lot: {reagent.reagent_lot}</div>}
                        {reagent.reagent_serial && <div>Serial: {reagent.reagent_serial}</div>}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-muted text-center py-4">No reagents available</div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons-section">
          <Link to={`/draft-edit?editid=${protocolId}`} className="btn btn-primary">
            <i className="bi bi-pencil me-2"></i>
            Edit Protocol
          </Link>
          {(protocol.is_live === 1 || protocol.is_live === '1') && (
            <Link to={`/play-simulation?id=${protocolId}`} className="btn btn-success">
              <i className="bi bi-play-fill me-2"></i>
              Play Simulation
            </Link>
          )}
        </div>
      </div>
    </QuratorLayout>
  );
};

export default DraftRead;
